import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Bell, Menu, X, Volume2, VolumeX, LogOut, LayoutDashboard, Shield, ChevronDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

function BellLogo({ onClick }) {
  return (
    <button onClick={onClick} data-testid="bell-logo" className="group flex items-center gap-2" aria-label="bell">
      <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-full bg-gradient-to-br from-marigold to-saffron temple-shadow gold-border">
        <svg width="24" height="24" viewBox="0 0 24 24" className="text-white group-active:animate-bell-swing">
          <path fill="currentColor" d="M12 2a1.5 1.5 0 0 1 1.5 1.5v.7A6 6 0 0 1 18 10v4l1.5 2.5a1 1 0 0 1-.86 1.5H5.36a1 1 0 0 1-.86-1.5L6 14v-4a6 6 0 0 1 4.5-5.8v-.7A1.5 1.5 0 0 1 12 2Z"/>
          <path fill="currentColor" d="M9.5 19.5a2.5 2.5 0 0 0 5 0Z"/>
        </svg>
      </span>
    </button>
  );
}

export default function Header() {
  const { t, lang, toggleLang, soundOn, toggleSound, playBell, notifData, refreshNotifications } = useApp();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { if (user) refreshNotifications(); }, [user, loc.pathname, refreshNotifications]);

  const links = [
    ["/", t("nav_home")], ["/temples", t("nav_temples")], ["/archakas", t("nav_archakas")],
    ["/post-vacancy", t("nav_post")], ["/about", t("nav_about")], ["/contact", t("nav_contact")],
  ];

  const doLogout = async () => { await logout(); nav("/"); };

  const NotifBell = () => (
    <DropdownMenu onOpenChange={(o) => { if (o) refreshNotifications(); }}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell" className="relative p-2 rounded-full hover:bg-marigold/15 transition-colors">
          <Bell className="w-5 h-5 text-maroon" />
          {notifData.unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-saffron text-white text-[10px] font-bold flex items-center justify-center">
              {notifData.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <DropdownMenuLabel className="font-telugu">{t("notifications")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notifData.items.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground font-telugu">{t("no_notifications")}</div>}
        {notifData.items.slice(0, 12).map((n, i) => (
          <div key={i} data-testid="notif-item" className={`px-3 py-2 border-b border-border/40 ${!n.read ? "bg-marigold/10" : ""}`}>
            <p className="text-sm font-semibold text-maroon font-telugu">{n.title}</p>
            <p className="text-xs text-muted-foreground font-telugu">{n.body}</p>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-parchment/90 border-b border-goldline/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-[68px]">
          <Link to="/" className="flex items-center gap-3" data-testid="header-home-link">
            <BellLogo onClick={playBell} />
            <div className="leading-tight">
              <div className="font-display text-lg sm:text-xl font-extrabold text-maroon" data-testid="header-brand-name">ArchakaVeda</div>
              <div className="text-[11px] text-saffron font-semibold font-telugu" data-testid="header-brand-sub">{t("site_name")}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map(([to, label]) => (
              <Link key={to} to={to} data-testid={`nav-${to}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium font-telugu transition-colors ${loc.pathname === to ? "text-saffron bg-marigold/15" : "text-maroon/80 hover:text-saffron hover:bg-marigold/10"}`}>
                {label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <button data-testid="lang-toggle" onClick={toggleLang}
              className="px-2.5 py-1.5 rounded-full text-xs font-bold border border-goldline/50 text-maroon hover:bg-marigold/15 transition-colors">
              {lang === "te" ? "EN" : "తె"}
            </button>
            <button data-testid="sound-toggle" onClick={toggleSound} className="p-2 rounded-full hover:bg-marigold/15 transition-colors" title="bell chime">
              {soundOn ? <Volume2 className="w-5 h-5 text-maroon" /> : <VolumeX className="w-5 h-5 text-muted-foreground" />}
            </button>
            {user && <NotifBell />}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu" className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full hover:bg-marigold/15 transition-colors">
                    <Avatar className="w-8 h-8 border border-goldline/50">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-saffron text-white text-xs">{(user.name || "U").slice(0, 2)}</AvatarFallback>
                    </Avatar>
                    <ChevronDown className="w-4 h-4 text-maroon hidden sm:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-telugu">{user.name}</div>
                    <div className="text-xs text-muted-foreground">{user.unique_id}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav("/dashboard")} className="font-telugu">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> {t("dashboard")}
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")} className="font-telugu">
                      <Shield className="w-4 h-4 mr-2" /> {t("admin_panel")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={doLogout} className="font-telugu text-destructive">
                    <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button data-testid="header-login" variant="ghost" size="sm" onClick={() => nav("/auth?mode=login")} className="text-maroon font-telugu">{t("login")}</Button>
                <Button data-testid="header-signup" size="sm" onClick={() => nav("/auth?mode=signup")} className="btn-sacred border-0 font-telugu">{t("signup")}</Button>
              </div>
            )}

            <button data-testid="mobile-menu-btn" className="lg:hidden p-2 text-maroon" onClick={() => setOpen((o) => !o)}>
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-goldline/40 bg-surface px-4 py-3 space-y-1">
          {links.map(([to, label]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)} data-testid={`mnav-${to}`}
              className="block px-3 py-2 rounded-lg text-maroon font-telugu hover:bg-marigold/10">{label}</Link>
          ))}
          {!user && (
            <div className="flex gap-2 pt-2">
              <Button data-testid="m-login" variant="outline" className="flex-1 font-telugu" onClick={() => { setOpen(false); nav("/auth?mode=login"); }}>{t("login")}</Button>
              <Button data-testid="m-signup" className="flex-1 btn-sacred border-0 font-telugu" onClick={() => { setOpen(false); nav("/auth?mode=signup"); }}>{t("signup")}</Button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
